<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const historyContainer = ref(null);
const navListRef = ref(null);
const progressWidth = ref(0);
const activeDecade = ref(props.data.decades[0]?.id);
const activeYear = ref(null);
const activeEventKey = ref(null);
const isNavSticky = ref(false);

let headerResizeObserver = null;
let lastScrollY = 0;
const headerHeight = ref(150);

const updateHeaderHeight = () => {
  const header = document.querySelector('header, .header, [data-header]');
  headerHeight.value = header ? header.offsetHeight + 150 : 150;
};

const getDecadeState = (decade) => {
  const decades = props.data.decades;
  const activeIdx = decades.findIndex((d) => d.id === activeDecade.value);
  const thisIdx = decades.findIndex((d) => d.id === decade.id);
  if (thisIdx < activeIdx) return 'past';
  if (thisIdx === activeIdx) return 'active';
  return 'future';
};

const shouldShowYear = (decadeIndex, eventIndex) => {
  const currentEvent = props.data.decades[decadeIndex].events[eventIndex];
  if (decadeIndex === 0 && eventIndex === 0) return true;
  let previousEvent;
  if (eventIndex > 0) {
    previousEvent = props.data.decades[decadeIndex].events[eventIndex - 1];
  } else {
    const prevDecade = props.data.decades[decadeIndex - 1];
    previousEvent = prevDecade.events[prevDecade.events.length - 1];
  }
  return currentEvent.year !== previousEvent.year;
};

// Scroll active decade into horizontal center of nav list
const centerActiveDecade = (decadeId) => {
  if (!navListRef.value) return;
  const list = navListRef.value;
  const btn = list.querySelector(`[data-decade-id="${decadeId}"]`);
  if (!btn) return;
  const listWidth = list.offsetWidth;
  const btnLeft = btn.offsetLeft;
  const btnWidth = btn.offsetWidth;
  const scrollTarget = btnLeft - listWidth / 2 + btnWidth / 2;
  list.scrollTo({ left: scrollTarget, behavior: 'smooth' });
};

const scrollToDecade = (id) => {
  const element = document.getElementById(`decade-${id}`);
  if (element) {
    const offset = 140;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

const handleScroll = () => {
  if (!historyContainer.value || !navListRef.value) return;

  const container = historyContainer.value;
  const list = navListRef.value;
  const navHeight =
    navListRef.value?.closest('.history__nav')?.offsetHeight || 0;
  const offset = headerHeight.value + navHeight;

  // Sticky nav: show when scrolling down, hide when scrolling up
  const currentScrollY = window.scrollY;
  const scrollingDown = currentScrollY > lastScrollY;
  const containerTop = container.getBoundingClientRect().top;
  const containerBottom = container.getBoundingClientRect().bottom;
  const isInsideBlock = containerTop <= 0 && containerBottom > 0;

  if (isInsideBlock) {
    isNavSticky.value = scrollingDown;
  } else {
    isNavSticky.value = false;
  }
  lastScrollY = currentScrollY;

  // Active decade detection
  const sections = Array.from(container.querySelectorAll('.decade-section'));
  let newActiveDecade = activeDecade.value;
  sections.forEach((section) => {
    const sRect = section.getBoundingClientRect();
    if (sRect.top < offset && sRect.bottom > offset) {
      newActiveDecade = section.getAttribute('data-id');
    }
  });

  if (newActiveDecade !== activeDecade.value) {
    activeDecade.value = newActiveDecade;
    centerActiveDecade(newActiveDecade);
  }

  // Progress line: smooth between decade button centers based on scroll position
  // within the current decade section
  const activeIdx = sections.findIndex(
    (s) => s.getAttribute('data-id') === activeDecade.value,
  );
  const nextIdx = activeIdx + 1;

  const activeSection = sections[activeIdx];
  const nextSection = sections[nextIdx] || null;

  if (activeSection) {
    const activeBtn = list.querySelector(
      `[data-decade-id="${activeDecade.value}"]`,
    );
    const nextDecadeId = nextSection
      ? nextSection.getAttribute('data-id')
      : null;
    const nextBtn = nextDecadeId
      ? list.querySelector(`[data-decade-id="${nextDecadeId}"]`)
      : null;

    const activeBtnCenter = activeBtn
      ? ((activeBtn.offsetLeft + activeBtn.offsetWidth / 2) /
          list.scrollWidth) *
        100
      : 0;
    const nextBtnCenter = nextBtn
      ? ((nextBtn.offsetLeft + nextBtn.offsetWidth / 2) / list.scrollWidth) *
        100
      : 100;

    // How far through the current decade section are we?
    const sectionTop = activeSection.getBoundingClientRect().top;
    const sectionHeight = activeSection.getBoundingClientRect().height;
    // sectionProgress: 0 when offset line hits top of section, 1 when it hits bottom
    const sectionProgress = Math.max(
      0,
      Math.min(1, (offset - sectionTop) / sectionHeight),
    );

    progressWidth.value =
      activeBtnCenter + (nextBtnCenter - activeBtnCenter) * sectionProgress;
  }

  // Active year detection (for year label color)
  const yearEls = container.querySelectorAll('.history-event__year');
  let newActiveYear = null;
  yearEls.forEach((el) => {
    const yRect = el.getBoundingClientRect();
    if (yRect.top < offset) {
      newActiveYear = el.textContent.trim();
    }
  });
  activeYear.value = newActiveYear;

  // Active event detection (per individual event card, not per year group)
  const eventEls = container.querySelectorAll('.history-event');
  let newActiveEventKey = null;
  eventEls.forEach((el) => {
    const eRect = el.getBoundingClientRect();
    if (eRect.top < offset) {
      newActiveEventKey = el.getAttribute('data-event-key');
    }
  });
  activeEventKey.value = newActiveEventKey;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
  nextTick(() => {
    centerActiveDecade(activeDecade.value);
    updateHeaderHeight();
    const header = document.querySelector('header, .header, [data-header]');
    if (header) {
      headerResizeObserver = new ResizeObserver(updateHeaderHeight);
      headerResizeObserver.observe(header);
    }
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  if (headerResizeObserver) headerResizeObserver.disconnect();
});
</script>

<template>
  <section
    :id="data.id"
    ref="historyContainer"
    :class="[
      'section',
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
      isNavSticky ? 'history--sticky' : '',
      'history',
      `history--${data.background_color}`,
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <nav
      class="history__nav"
      :class="{ 'history__nav--sticky': isNavSticky }"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
    >
      <div class="container">
        <div class="history__nav-inner">
          <div
            ref="navListRef"
            class="history__nav-list"
            :style="{ '--progress': progressWidth + '%' }"
          >
            <button
              v-for="decade in data.decades"
              :key="decade.id"
              :data-decade-id="decade.id"
              class="history__nav-item h6-desktop"
              :class="[
                `history__nav-item--${getDecadeState(decade)}`,
                `js-button-history-${getDecadeState(decade)}`,
              ]"
              @click="scrollToDecade(decade.id)"
            >
              {{ decade.label }}
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div class="container">
      <div class="history__content">
        <div
          v-for="(decade, dIdx) in data.decades"
          :id="`decade-${decade.id}`"
          :key="decade.id"
          :data-id="decade.id"
          class="decade-section"
        >
          <div
            v-for="(event, eIdx) in decade.events"
            :key="eIdx"
            class="history-event"
            :data-event-key="`${decade.id}:${eIdx}`"
            :class="{
              'history-event--no-year': !shouldShowYear(dIdx, eIdx),
              'history-event--active':
                activeEventKey === `${decade.id}:${eIdx}`,
            }"
          >
            <div
              class="history-event__year-col"
              data-aos="fade-up"
              :data-aos-delay="eIdx >= 1 ? 100 + 100 * eIdx : 100"
            >
              <p
                v-if="shouldShowYear(dIdx, eIdx)"
                class="mb-0 history-event__year h2"
                :class="{
                  'history-event__year--active':
                    String(event.year) === String(activeYear),
                }"
              >
                {{ event.year }}
              </p>
            </div>
            <div class="history-event__card">
              <div
                v-if="event.image"
                class="history-event__media"
                data-aos="zoom-in"
                data-aos-easing="ease-out-quad"
                :data-aos-delay="eIdx >= 1 ? 100 + 100 * eIdx : 100"
              >
                <NuxtImg
                  v-if="event?.image?.src"
                  :src="event.image.src"
                  :alt="event.image.alt"
                  :height="event.image.height"
                  :width="event.image.width"
                  class="history-event__image"
                  loading="lazy"
                />
              </div>
              <div
                v-if="event.text"
                class="history-event__info body-1-regular"
                data-aos="fade-up"
                :data-aos-delay="eIdx >= 1 ? 100 + 100 * eIdx : 100"
                v-html="event.text"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.history {
  $parent: &;
  display: grid;
  overflow: visible;
  gap: 80px;
  @include media-breakpoint-up(sm) {
    gap: 120px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 120);
  }
  &--sticky {
    z-index: 1000;
  }
  &--white {
    #{$parent}__nav {
      &:after {
        background: linear-gradient(
          0deg,
          #ffffff 54.26%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }
  }
  &--gray {
    #{$parent}__nav {
      &:after {
        background: linear-gradient(
          0deg,
          $gray 54.26%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }
  }
  &--black {
    #{$parent}__nav {
      &:after {
        background: linear-gradient(
          0deg,
          $surface-container-opacity 54.26%,
          rgba(255, 255, 255, 0) 100%
        );
      }
    }
  }

  &__nav {
    position: sticky;
    top: -200px;
    z-index: 100;
    padding: 16px 0 0;
    transition: top 0.3s ease;
    @include media-breakpoint-up(md) {
      padding: 24px 0 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(top, -200);
    }
    &:after {
      content: '';
      height: 140px;
      left: 0;
      position: absolute;
      top: 0;
      transform: matrix(1, 0, 0, -1, 0, 0);
      width: 100%;
      @include media-breakpoint-up(md) {
        height: 180px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 180);
      }
    }
    &--sticky {
      top: 0;
    }

    &-inner {
    }

    &-list {
      display: flex;
      justify-content: space-between;
      gap: 32px;
      overflow-x: auto;
      scrollbar-width: none;
      scroll-behavior: smooth;
      background-image: linear-gradient(
          to right,
          $primary var(--progress, 0%),
          transparent var(--progress, 0%)
        ),
        linear-gradient(to right, $divider 100%, transparent 100%);
      background-repeat: no-repeat;
      background-position: left 44px;
      background-size: 100% 1px;
      @include media-breakpoint-up(sm) {
        gap: 70px;
      }
      @include media-breakpoint-up(md) {
        gap: 136px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 136);
        background-position: left px-to-vw(44);
        background-size: 100% px-to-vw(1);
      }
      &::-webkit-scrollbar {
        display: none;
      }
    }

    &-item {
      display: grid;
      background: none;
      border: none;
      cursor: pointer;
      height: 72px;
      min-width: 88px;
      padding: 2px 0 0 0;
      position: relative;
      transition: color 0.3s;
      white-space: nowrap;
      width: 88px;
      flex-shrink: 0;
      @include media-breakpoint-up(sm) {
        height: 88px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 88);
        @include responsive-spacing(min-width, 88);
        @include responsive-spacing(width, 88);
        @include responsive-spacing(padding-top, 2);
      }
      &:after {
        bottom: 24px;
        content: '';
        height: 8px;
        left: 0;
        margin: 0 auto;
        position: absolute;
        right: 0;
        width: 8px;
        transition: background-color 0.3s;
        @include media-breakpoint-up(sm) {
          bottom: 40px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(bottom, 40);
          @include responsive-spacing(height, 8);
          @include responsive-spacing(width, 8);
        }
      }
      &--future {
        color: $on-surface-variant-2;

        &:after {
          background-color: $on-surface-variant-2;
        }
      }
      &--past {
        color: $black;

        &:after {
          background-color: $black;
        }
      }
      &--active {
        color: $black;

        &:after {
          background-color: $primary;
        }
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 56px;

    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }
  }

  .decade-section {
    scroll-margin-top: 56px;
    display: flex;
    flex-direction: column;
    gap: 56px;

    @include media-breakpoint-up(sm) {
      gap: 80px;
      scroll-margin-top: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
      @include responsive-spacing(scroll-margin-top, 80);
    }
  }

  &-event {
    display: grid;
    gap: 56px;
    grid-template-columns: 1fr;
    opacity: 0.5;
    transition: opacity 0.5s linear;
    @include media-breakpoint-up(sm) {
      gap: 80px;
      padding: 0 4px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 3.5fr 8.5fr;
      padding: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }

    &--no-year {
      margin-top: -58px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(margin-top, -40);
      }
    }

    &--active {
      opacity: 1;
    }

    &__card {
      display: grid;
      grid-template-columns: 1fr;
      gap: 32px;

      @include media-breakpoint-up(sm) {
        gap: 56px;
        grid-template-columns: 5.25fr 6.75fr;
        padding-top: 8px;
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: 6.3fr 5.7fr;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 56);
        @include responsive-spacing(padding-top, 8);
      }
    }

    &__year {
      position: sticky;
      top: 0;
      transition: color 0.3s;
    }

    &__media {
      overflow: hidden;
    }

    &__image {
      border-radius: 8px;
      height: auto;
      width: 100%;

      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
      }
    }

    &__info {
      &:deep(p:last-child) {
        margin: 0;
      }
    }
  }
}
</style>
